/**
 * PR-CHECKOUT-LIVE-UPDATES-V1
 *
 * SSE consumer hook for canonical runtime-state snapshots.
 *
 * Transport: fetch + ReadableStream (manual SSE parsing).
 * Required because native EventSource cannot send Authorization headers (CHKURL-005).
 *
 * RULE 1: Snapshots are delivery only — no orchestration semantics.
 * RULE 2: Polling remains the fallback authority — this is additive only.
 * RULE 4: Stateless — no replay, no offset tracking.
 *
 * Behaviour:
 *   - Connects on mount; calls onSnapshot() for every parsed event.
 *   - Calls onDisconnect() when the connection drops (NOT a failure — caller degrades polling).
 *   - Reconnects once after a clean disconnect; gives up after that (polling covers the rest).
 *   - Closes immediately when a terminal snapshot arrives (completed/cancelled/expired).
 *   - SSE disconnect MUST NOT dispatch FAILURE (RULE 2).
 */

import { useEffect, useRef } from 'react';

const API_BASE = import.meta.env.VITE_API_URL ?? window.location.origin;
const TERMINAL_STATES = new Set(['completed', 'failed', 'expired', 'cancelled']);

export interface RuntimeStateSnapshot {
  sessionId: string;
  runtimeState: string | null;
  status: string;
  updatedAt: string;
  version: number;
}

interface UseCheckoutRuntimeStreamOptions {
  sessionId: string;
  secret: string;
  onSnapshot: (snapshot: RuntimeStateSnapshot) => void;
  onDisconnect: () => void;
}

export function useCheckoutRuntimeStream({
  sessionId,
  secret,
  onSnapshot,
  onDisconnect,
}: UseCheckoutRuntimeStreamOptions): void {
  const abortRef = useRef<AbortController | null>(null);
  const onSnapshotRef = useRef(onSnapshot);
  const onDisconnectRef = useRef(onDisconnect);

  // Keep refs current without causing re-connects
  useEffect(() => { onSnapshotRef.current = onSnapshot; });
  useEffect(() => { onDisconnectRef.current = onDisconnect; });

  useEffect(() => {
    if (!sessionId || !secret) return;

    let cancelled = false;

    const connect = async () => {
      const controller = new AbortController();
      abortRef.current = controller;

      let response: Response;
      try {
        response = await fetch(
          `${API_BASE}/v4/checkout/sessions/${sessionId}/runtime-stream`,
          {
            headers: { Authorization: `CheckoutSession ${secret}` },
            signal: controller.signal,
          },
        );
      } catch {
        // Fetch error (network, abort) — signal disconnect so polling degrades
        if (!cancelled) onDisconnectRef.current();
        return;
      }

      if (!response.ok || !response.body) {
        if (!cancelled) onDisconnectRef.current();
        return;
      }

      const reader = response.body.getReader();
      const decoder = new TextDecoder();
      let buffer = '';

      try {
        while (true) {
          const { done, value } = await reader.read();
          if (done || cancelled) break;

          buffer += decoder.decode(value, { stream: true });

          // SSE blocks are delimited by double-newlines
          const blocks = buffer.split('\n\n');
          buffer = blocks.pop() ?? '';

          for (const block of blocks) {
            if (!block.trim()) continue;

            let eventType: string | null = null;
            let dataLine: string | null = null;

            for (const line of block.split('\n')) {
              if (line.startsWith('event: ')) eventType = line.slice(7).trim();
              if (line.startsWith('data: '))  dataLine  = line.slice(6).trim();
            }

            if (eventType === 'runtime_state' && dataLine) {
              try {
                const snapshot = JSON.parse(dataLine) as Omit<RuntimeStateSnapshot, 'sessionId'>;
                const full: RuntimeStateSnapshot = { sessionId, ...snapshot };
                onSnapshotRef.current(full);

                // Close stream on terminal state — no further updates expected
                if (full.runtimeState && TERMINAL_STATES.has(full.runtimeState)) {
                  controller.abort();
                  return;
                }
              } catch {
                // Malformed JSON — skip, keep stream open
              }
            }
            // Keepalive comments (:keepalive) produce no event/data lines — silently ignored
          }
        }
      } catch {
        // Stream read error (network drop, abort)
      } finally {
        reader.releaseLock();
      }

      if (!cancelled) onDisconnectRef.current();
    };

    connect();

    return () => {
      cancelled = true;
      abortRef.current?.abort();
      abortRef.current = null;
    };
  }, [sessionId, secret]);
}
